/* eslint-disable sonarjs/cognitive-complexity */
import T from '@/components/Helpers/Translate'
import { useEffect, useState } from 'react'

import type {
  CosmicAttributeType,
  CosmicVariationType,
} from '@/lib/cms/cosmic/types/CosmicProductType'
import FlavorTile from '@/components/_ui/FlavorTile'
import { getIfVariationHasFlavor } from '@/lib/utils/product/getIfVariationHasFlavor'

type Props = {
  attributes?: CosmicAttributeType[]
  variations?: CosmicVariationType[]
  selectedVariation: CosmicVariationType
  setSelectedVariation: (variation: CosmicVariationType) => void
}

export const ProductFlavorSelector = ({
  variations,
  selectedVariation,
  setSelectedVariation,
  attributes,
}: Props) => {
  const [productHasMultipleVariations, setProductHasMultipleVariations] =
    useState(false)

  useEffect(() => {
    if (!variations) return

    if (variations.length > 1) {
      setProductHasMultipleVariations(true)
    }
  }, [variations])

  const handleChangeFlavor = ({ newFlavor }: { newFlavor: string }) => {
    if (!attributes || !variations) return

    if (attributes.length === 1) {
      const newVariation = variations.find((variation) =>
        variation.combinationName.split('/').includes(newFlavor),
      )

      if (newVariation) {
        setSelectedVariation(newVariation)
        return
      }
    }

    const attributeType = attributes.find((attribute) =>
      attribute.options.some((opt) => opt.name === newFlavor),
    )
    if (!attributeType) return

    const currentFlavors = selectedVariation.combinationName.split('/')
    const updatedFlavors = currentFlavors.map((currentFlavor) => {
      const isSameType = attributeType.options.some(
        (opt) => opt.name === currentFlavor,
      )
      return isSameType ? newFlavor : currentFlavor
    })

    const newVariation = variations.find((variation) => {
      const variationFlavors = variation.combinationName.split('/')
      return (
        variationFlavors.length === updatedFlavors.length &&
        updatedFlavors.every((flavor) => variationFlavors.includes(flavor))
      )
    })

    if (newVariation) {
      setSelectedVariation(newVariation)
    }
  }

  if (!attributes || !productHasMultipleVariations) return null

  return (
    <div className="flex w-full flex-col text-primary">
      <span className="pb-4 font-semibold">
        <T>customize_pack</T>
      </span>

      {attributes.map((attribute) => (
        <div className="mb-5" key={attribute.name}>
          <div className="mb-5">
            <span className="text-sm font-semibold text-[#0B3B61B2]">
              {attribute.name}{' '}
            </span>
            <span className="text-sm font-semibold text-primary">
              {selectedVariation.combinationName
                .split('/')
                .find((flavor) =>
                  attribute.options.some((opt) => opt.name === flavor),
                )}
            </span>
          </div>
          <div className="flex flex-row flex-wrap gap-5">
            {attribute.options.map((flavor) => (
              <FlavorTile
                key={flavor.name}
                isSelected={getIfVariationHasFlavor({
                  variation: selectedVariation,
                  flavor: flavor.name,
                })}
                flavorColors={[]}
                // flavorColors={flavor.flavorColors?.map((color) => color.color)}
                name={flavor.name}
                imageUrl={flavor.icon}
                isStarred={flavor.isHighlight}
                data-test={`product-flavor-${flavor.name}`}
                onClick={() =>
                  handleChangeFlavor({
                    newFlavor: flavor.name,
                  })
                }
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
